import { useState, useEffect, useCallback, useRef } from 'react';
import { api, GrLeaderboardGameInfo } from '@/lib/api';

interface UseLeaderboardGamesReturn {
  games: GrLeaderboardGameInfo[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useLeaderboardGames(): UseLeaderboardGamesReturn {
  const [games, setGames] = useState<GrLeaderboardGameInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const fetchIdRef = useRef(0);

  const fetch = useCallback(async () => {
    const id = ++fetchIdRef.current;
    try {
      setLoading(true);
      const result = await api.getGameRewardsLeaderboardGames();
      if (fetchIdRef.current !== id) return;
      setGames(result.games);
    } catch (err) {
      if (fetchIdRef.current !== id) return;
      setError(err instanceof Error ? err.message : 'Failed to load leaderboard games');
    } finally {
      if (fetchIdRef.current === id) {
        setLoading(false);
      }
    }
  }, []);

  const refresh = useCallback(() => {
    setError(null);
    fetch();
  }, [fetch]);

  useEffect(() => {
    refresh();
    return () => {
      fetchIdRef.current++;
    };
  }, [refresh]);

  return { games, loading, error, refresh };
}
